/**
 * Reusable filter UI — chips + popover-based multi-select + active-filter
 * strip + Clear all. Mirrors the pattern Linear / Notion / Asana use so
 * a Company Admin can stack filters without nested dropdowns.
 *
 * Three exported pieces:
 *   - FilterBar       — wraps chips, lays out the active-filter strip, owns
 *                       the "Clear all" button.
 *   - FilterChip      — pill button + popover with search + checkbox list.
 *                       Multi-select. Headless about state — caller controls
 *                       the selected Set and the available options.
 *   - DateRangeChip   — same shape as FilterChip but the popover has two
 *                       date inputs instead of a checkbox list.
 *
 * Each chip is fully keyboard-navigable and closes on outside click /
 * Escape.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search, X } from 'lucide-react'

// --------- FilterBar ---------

/**
 * @param {{
 *   children: React.ReactNode,
 *   activeFilters: Array<{ key: string, label: string, onRemove: () => void }>,
 *   onClearAll?: () => void,
 *   trailing?: React.ReactNode,
 * }} props
 */
export function FilterBar({ children, activeFilters, onClearAll, trailing }) {
  const hasActive = activeFilters.length > 0
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        {children}
        {trailing ? (
          <div className="ml-auto flex items-center gap-2">{trailing}</div>
        ) : null}
      </div>

      {hasActive ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold text-[color:var(--color-ink-muted)] mr-1">
            Filtering by:
          </span>
          {activeFilters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={f.onRemove}
              className="group inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] text-sm font-medium cursor-pointer hover:bg-[color:var(--color-accent-bg-strong)]"
              aria-label={`Remove filter: ${f.label}`}
              title="Remove this filter"
            >
              <span className="truncate max-w-[260px]">{f.label}</span>
              <X
                size={13}
                className="text-[color:var(--color-accent-strong)] opacity-70 group-hover:opacity-100"
              />
            </button>
          ))}
          {onClearAll ? (
            <button
              type="button"
              onClick={onClearAll}
              className="ml-1 h-7 px-2 rounded-full text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              Clear All
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

// --------- FilterChip (multi-select) ---------

/**
 * @param {{
 *   label: string,
 *   icon?: React.ComponentType<{size?:number, className?:string}>,
 *   options: Array<{ value: string, label: string, hint?: string, swatch?: string }>,
 *   selected: Set<string>,
 *   onChange: (next: Set<string>) => void,
 *   searchPlaceholder?: string,
 *   emptyText?: string,
 * }} props
 */
export function FilterChip({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  searchPlaceholder = 'Search…',
  emptyText = 'No records found.',
}) {
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const inputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [coords, setCoords] = useState(null)
  const popoverId = useId()

  // Compute popover position relative to the trigger, and keep it glued
  // to the trigger while open. The popover is `position: fixed` (portaled
  // to <body>), so if the page — or an inner scroll container like the
  // Task Monitor content area — scrolls, its coordinates go stale and it
  // detaches, floating over the table. Recompute on scroll (capture: true
  // so it catches scroll on ANY ancestor scroll container, not just
  // window) and on resize.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    function updateCoords() {
      const r = triggerRef.current?.getBoundingClientRect()
      if (r) setCoords({ top: r.bottom + 6, left: r.left })
    }
    updateCoords()
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open])

  // Outside click + Escape close.
  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (triggerRef.current?.contains(e.target)) return
      if (popoverRef.current?.contains(e.target)) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the search input on open + reset query on close.
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(t)
    } else {
      setQuery('')
    }
  }, [open])

  const filtered = options.filter(
    (o) =>
      query.trim() === '' ||
      o.label.toLowerCase().includes(query.trim().toLowerCase()),
  )

  const toggle = useCallback(
    (value) => {
      const next = new Set(selected)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      onChange(next)
    },
    [selected, onChange],
  )

  const count = selected.size
  // Chip label varies with selection: "Status" → "Status: Urgent" → "Status: 3"
  let summary = label
  if (count === 1) {
    const onlyValue = [...selected][0]
    const opt = options.find((o) => o.value === onlyValue)
    summary = `${label}: ${opt?.label ?? onlyValue}`
  } else if (count > 1) {
    summary = `${label}: ${count}`
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm cursor-pointer transition-colors ' +
          (count > 0
            ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)] font-semibold'
            : 'bg-[color:var(--color-card)] text-[color:var(--color-ink)] border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        {Icon ? <Icon size={14} className="shrink-0" /> : null}
        <span className="truncate max-w-[220px]">{summary}</span>
        <ChevronDown
          size={13}
          className={
            'shrink-0 transition-transform ' + (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              id={popoverId}
              role="dialog"
              aria-label={`Filter by ${label}`}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
              }}
              className="w-72 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
            >
              <div className="p-2 border-b border-[color:var(--color-rule)]">
                <div className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
                  >
                    <Search size={14} />
                  </span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="fd-search-pill w-full pl-9 pr-3 h-8 text-sm rounded-full text-[color:var(--color-ink)] placeholder-[color:var(--color-ink-muted)] outline-none"
                  />
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <p className="px-3 py-3 text-sm italic text-[color:var(--color-ink-muted)]">
                    {emptyText}
                  </p>
                ) : (
                  filtered.map((o) => {
                    const checked = selected.has(o.value)
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={checked}
                        onClick={() => toggle(o.value)}
                        className={
                          'w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm cursor-pointer ' +
                          (checked
                            ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]'
                            : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                        }
                      >
                        <span
                          className={
                            'inline-flex items-center justify-center h-4 w-4 rounded border ' +
                            (checked
                              ? 'bg-[color:var(--color-accent-strong)] border-[color:var(--color-accent-strong)]'
                              : 'border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)]')
                          }
                          aria-hidden="true"
                        >
                          {checked ? (
                            <Check size={11} className="text-white" strokeWidth={3} />
                          ) : null}
                        </span>
                        {o.swatch ? (
                          <span
                            className="h-2.5 w-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: o.swatch }}
                            aria-hidden="true"
                          />
                        ) : null}
                        <span className="flex-1 truncate">{o.label}</span>
                        {o.hint ? (
                          <span className="text-sm text-[color:var(--color-ink-muted)]">
                            {o.hint}
                          </span>
                        ) : null}
                      </button>
                    )
                  })
                )}
              </div>
              {count > 0 ? (
                <div className="border-t border-[color:var(--color-rule)] p-2 flex items-center justify-between bg-[color:var(--color-canvas)]">
                  <span className="text-sm text-[color:var(--color-ink-muted)]">
                    {count} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => onChange(new Set())}
                    className="text-sm text-[color:var(--color-ink-strong)] hover:underline cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

// --------- DateRangeChip ---------

/** Quick-preset compute helpers used inside the date-range popover.
 *  Each compute() returns `{ from, to }` with ISO YYYY-MM-DD strings. */
function isoDate(d) {
  // Local-tz friendly — browsers' native date input is local-tz, so
  // we serialise the local Y/M/D, NOT toISOString() (which is UTC and
  // can flip the day across midnight).
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const DATE_PRESETS = [
  {
    label: 'Today',
    compute: () => {
      const t = new Date()
      const iso = isoDate(t)
      return { from: iso, to: iso }
    },
  },
  {
    label: 'Yesterday',
    compute: () => {
      const t = new Date()
      t.setDate(t.getDate() - 1)
      const iso = isoDate(t)
      return { from: iso, to: iso }
    },
  },
  {
    label: 'This week',
    compute: () => {
      const t = new Date()
      const day = t.getDay() // 0=Sun..6=Sat
      const monday = new Date(t)
      monday.setDate(t.getDate() - ((day + 6) % 7))
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      return { from: isoDate(monday), to: isoDate(sunday) }
    },
  },
  {
    label: 'This month',
    compute: () => {
      const t = new Date()
      const first = new Date(t.getFullYear(), t.getMonth(), 1)
      const last = new Date(t.getFullYear(), t.getMonth() + 1, 0)
      return { from: isoDate(first), to: isoDate(last) }
    },
  },
]


/**
 * Date-range filter chip — popover with two `<input type="date">` fields.
 * State shape is `{ from?: string, to?: string }` (ISO YYYY-MM-DD).
 *
 * @param {{
 *   label: string,
 *   icon?: React.ComponentType<{size?:number, className?:string}>,
 *   value: { from?: string, to?: string },
 *   onChange: (next: { from?: string, to?: string }) => void,
 * }} props
 */
export function DateRangeChip({ label, icon: Icon, value, onChange }) {
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const popoverId = useId()

  // Keep the fixed-positioned popover glued to its trigger while open —
  // recompute on scroll (capture: true catches inner scroll containers,
  // e.g. the Task Monitor content area) and resize so it never detaches.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    function updateCoords() {
      const r = triggerRef.current?.getBoundingClientRect()
      if (r) setCoords({ top: r.bottom + 6, left: r.left })
    }
    updateCoords()
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (triggerRef.current?.contains(e.target)) return
      if (popoverRef.current?.contains(e.target)) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const hasValue = Boolean(value.from || value.to)
  let summary = label
  if (value.from && value.to) summary = `${label}: ${value.from} → ${value.to}`
  else if (value.from) summary = `${label}: from ${value.from}`
  else if (value.to) summary = `${label}: until ${value.to}`

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm cursor-pointer transition-colors ' +
          (hasValue
            ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)] font-semibold'
            : 'bg-[color:var(--color-card)] text-[color:var(--color-ink)] border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]')
        }
      >
        {Icon ? <Icon size={14} className="shrink-0" /> : null}
        <span className="truncate max-w-[260px]">{summary}</span>
        <ChevronDown
          size={13}
          className={
            'shrink-0 transition-transform ' + (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              id={popoverId}
              role="dialog"
              aria-label={`Filter by ${label}`}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
              }}
              className="w-80 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
            >
              {/* Header — gives the popover a clear top edge so the
                  "From" label doesn't crowd the rounded corner. */}
              <div className="px-4 py-2.5 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                  {label} date range
                </p>
              </div>

              {/* Quick presets */}
              <div className="px-4 pt-3 pb-2 grid grid-cols-2 gap-2">
                {DATE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => onChange(p.compute())}
                    className="h-8 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Manual From / To. The visible "DD-MM-YYYY" hint sits above
                  the input so it's always visible — native date inputs only
                  surface a format hint when empty AND the browser's locale
                  exposes one, which Safari and some Firefox setups don't. */}
              <div className="px-4 pb-4">
                <div className="flex items-baseline justify-between mb-1 mt-2">
                  <label className="text-sm font-semibold text-[color:var(--color-ink-muted)]">
                    From
                  </label>
                  <span className="text-sm text-[color:var(--color-ink-subtle)] tabular-nums">
                    DD-MM-YYYY
                  </span>
                </div>
                <input
                  type="date"
                  value={value.from ?? ''}
                  max={value.to || undefined}
                  onChange={(e) =>
                    onChange({ ...value, from: e.target.value || undefined })
                  }
                  className="w-full h-9 px-2 mb-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm outline-none focus:border-[color:var(--color-accent)]"
                />
                <div className="flex items-baseline justify-between mb-1">
                  <label className="text-sm font-semibold text-[color:var(--color-ink-muted)]">
                    To
                  </label>
                  <span className="text-sm text-[color:var(--color-ink-subtle)] tabular-nums">
                    DD-MM-YYYY
                  </span>
                </div>
                <input
                  type="date"
                  value={value.to ?? ''}
                  min={value.from || undefined}
                  onChange={(e) =>
                    onChange({ ...value, to: e.target.value || undefined })
                  }
                  className="w-full h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm outline-none focus:border-[color:var(--color-accent)]"
                />
              </div>

              {hasValue ? (
                <div className="border-t border-[color:var(--color-rule)] px-4 py-2 flex items-center justify-between bg-[color:var(--color-canvas)]">
                  <span className="text-sm text-[color:var(--color-ink-muted)]">
                    {value.from && value.to
                      ? `${value.from} → ${value.to}`
                      : value.from
                        ? `from ${value.from}`
                        : `until ${value.to}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => onChange({})}
                    className="text-sm text-[color:var(--color-ink-strong)] hover:underline cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

// --------- Search input (companion to FilterChip) ---------

/**
 * Free-text search input — kept here so FilterBar pages can compose
 * a single visual unit. Use independently or alongside FilterChip.
 */
export function FilterSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="relative w-56">
      <span
        aria-hidden="true"
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
      >
        <Search size={15} />
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="fd-search-pill h-9 pl-9 pr-4 w-full rounded-full text-[color:var(--color-ink)] text-sm outline-none"
      />
    </div>
  )
}
